import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Modal from "react-bootstrap/Modal";
import "./ClassificationWorkflow.css";

const token = () => ({ requesterToken: Cookies.get("user_token") });
const errorText = error => error.response?.data?.error || error.response?.data || error.message;
const modelLabels = {dinov2: "DINOv2 Large", dinov2_base: "DINOv2 ViT-B/14", dinov3: "DINOv3 ViT-B/16"};

export function useCatalog(active = true) {
  const [catalog, setCatalog] = useState({datasets: [], tables: [], models: []});
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!active) return;
    let live = true;
    axios.get("/classification/catalog", {params: token()})
      .then(({data}) => { if (live) { setCatalog(data); setError(""); } })
      .catch(e => { if (live) setError(String(errorText(e))); });
    return () => { live = false; };
  }, [active, revision]);
  const refresh = useCallback(() => setRevision(v => v + 1), []);
  return {catalog, error, refresh};
}

export function useJob(onFinished) {
  const [job, setJob] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const finished = useRef(onFinished); finished.current = onFinished;
  useEffect(() => {
    if (!job) return;
    let live = true, timer;
    const poll = async () => {
      try {
        const {data} = await axios.get(`/classification/jobs/${job}`, {params: token()});
        if (!live) return;
        setStatus(data.status);
        if (data.status === "finished") { setJob(null); finished.current(data.result); }
        else if (["failed", "stopped", "canceled"].includes(data.status)) {
          setError(data.error || "Classification task failed"); setJob(null);
        } else timer = setTimeout(poll, 3000);
      } catch (e) { if (live) { setError(String(errorText(e))); setJob(null); } }
    };
    poll();
    return () => { live = false; clearTimeout(timer); };
  }, [job]);
  return {busy: !!job, status, error, start: id => {setError(""); setStatus("queued"); setJob(id);}};
}

export function ClassificationImport() {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const job = useJob(setResult);
  const submit = async event => {
    event.preventDefault(); setSubmitting(true); setError(""); setResult(null);
    const body = new FormData(); body.append("file", file); body.append("dataset_name", name);
    body.append("requesterToken", Cookies.get("user_token") || "");
    try { const {data} = await axios.post("/classification/import", body); job.start(data.job_id); }
    catch (e) { setError(String(errorText(e))); }
    finally { setSubmitting(false); }
  };
  return <details className="classification-workflow">
    <summary>Import Classification Dataset</summary>
    <p>Upload a ZIP containing one folder per class. Folder names become image labels.</p>
    <form onSubmit={submit}><fieldset disabled={submitting || job.busy}>
      <label>Classification dataset name<input required value={name} pattern="[A-Za-z][A-Za-z0-9_.-]{0,99}" onChange={e => setName(e.target.value)} /></label>
      <label>Classification ZIP<input required type="file" accept=".zip" onChange={e => setFile(e.target.files[0])} /></label>
      <button className="org-btn org-btn-primary" type="submit">Import</button>
    </fieldset></form>
    {job.status && <p role="status">Import: {job.status}</p>}
    {(error || job.error) && <p role="alert">{error || job.error}</p>}
    {result && <div><p>Imported {result.samples} images into {result.dataset_name}.</p>
      <p>{Object.entries(result.class_counts).map(([label, count]) => `${label}: ${count}`).join(", ")}</p>
      <a href="/pre-label">Open Pre-Label to extract embeddings</a></div>}
  </details>;
}

export function EmbeddingTableExport({datasetName, model}) {
  const [open, setOpen] = useState(false);
  const {catalog, error: catalogError} = useCatalog(open);
  const [field, setField] = useState("ground_truth");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const job = useJob(setResult);
  const dataset = catalog.datasets.find(d => d.name === datasetName);
  const fields = dataset?.label_fields || [];
  const submit = async event => {
    event.preventDefault(); setSubmitting(true); setError(""); setResult(null);
    try {
      const {data} = await axios.post("/classification/tables", {...token(), dataset_name: datasetName,
        model, label_field: field, table_name: name});
      job.start(data.job_id);
    } catch (e) { setError(String(errorText(e))); }
    finally { setSubmitting(false); }
  };
  return <>
    <button type="button" className="org-btn org-btn-secondary" onClick={() => setOpen(true)}>Save Embeddings to Table</button>
    <Modal show={open} onHide={() => setOpen(false)}>
      <Modal.Header closeButton><Modal.Title>Save Embeddings to Table</Modal.Title></Modal.Header>
      <Modal.Body><p>{datasetName} · {modelLabels[model]}</p>
        <form onSubmit={submit}><fieldset disabled={submitting || job.busy}>
          <label>Classification label field<select value={field} onChange={e => setField(e.target.value)}>
            <option value="">Choose label field</option>{fields.map(f => <option key={f}>{f}</option>)}
          </select></label>
          <label>New embedding table name<input required pattern="[A-Za-z][A-Za-z0-9_.-]{0,99}" value={name} onChange={e => setName(e.target.value)} /></label>
          <button className="org-btn org-btn-primary" type="submit" disabled={!fields.includes(field)}>Save Table</button>
        </fieldset></form>
        {job.status && <p role="status">Save Table: {job.status}</p>}
        {(error || job.error || catalogError) && <p role="alert">{error || job.error || catalogError}</p>}
        {result && <p>Saved {result.samples} images to {result.table_name}. <a href="/train">Open Train</a></p>}
      </Modal.Body>
    </Modal>
  </>;
}

export function DinoEmbeddingETL({onCompleted}) {
  const {catalog, error: catalogError, refresh} = useCatalog();
  const initial = useRef(new URLSearchParams(window.location.search));
  const [datasetName, setDatasetName] = useState(initial.current.get("dino_dataset") || "");
  const [model, setModel] = useState(initial.current.get("dino_model") || "dinov2_base");
  const [field, setField] = useState("ground_truth");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const job = useJob(value => {setResult(value); refresh(); onCompleted?.();});
  const dataset = catalog.datasets.find(d => d.name === datasetName);
  const fields = dataset?.label_fields || [];
  const ready = !!dataset && fields.includes(field) && dataset.embeddings.includes(model);
  const submit = async event => {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true); setError(""); setResult(null);
    try {
      const {data} = await axios.post("/classification/tables", {...token(), dataset_name: datasetName,
        model, label_field: field, table_name: name});
      job.start(data.job_id);
    } catch (e) {setError(String(errorText(e)));}
    finally {setSubmitting(false);}
  };
  return <details className="classification-workflow" open={initial.current.has("dino_dataset") || undefined}>
    <summary>DINO Embedding → Table</summary>
    <p>Convert classification labels and existing DINO embeddings into a new table for classification training.</p>
    <button type="button" onClick={refresh}>Refresh datasets and models</button>
    <form onSubmit={submit}><fieldset disabled={submitting || job.busy}>
      <label>Embedding source dataset<select required value={datasetName} onChange={e => {setDatasetName(e.target.value); setField("ground_truth");}}>
        <option value="">Choose dataset</option>{catalog.datasets.map(d => <option key={d.name}>{d.name}</option>)}</select></label>
      <label>Embedding DINO model<select value={model} onChange={e => setModel(e.target.value)}>
        {catalog.models.map(m => <option key={m.id} value={m.id}>{modelLabels[m.id] || m.model}</option>)}</select></label>
      <label>Embedding classification label field<select required value={field} onChange={e => setField(e.target.value)}>
        <option value="">Choose label field</option>{fields.map(f => <option key={f}>{f}</option>)}</select></label>
      <label>Target embedding table name<input required pattern="[A-Za-z][A-Za-z0-9_.-]{0,99}" value={name} onChange={e => setName(e.target.value)} /></label>
      <button className="org-btn org-btn-primary" type="submit" disabled={!ready}>Create Embedding Table</button>
    </fieldset></form>
    {dataset && !dataset.embeddings.includes(model) && <p>Extract embeddings for this model under Pre-Label → DINO first.</p>}
    <p>Class names are read from the selected label field. No manual class entry is needed.</p>
    {job.status && <p role="status">Embedding ETL: {job.status}</p>}
    {(error || job.error || catalogError) && <p role="alert">{error || job.error || catalogError}</p>}
    {result && <p>Created {result.table_name} with {result.samples} images. <a href="/train">Open Train</a></p>}
  </details>;
}

export function DinoClassificationTrain({embedded = false, onSubmitted}) {
  const {catalog, error: catalogError, refresh} = useCatalog();
  const [source, setSource] = useState("dataset");
  const [dataset, setDataset] = useState("");
  const [table, setTable] = useState("");
  const [field, setField] = useState("ground_truth");
  const [model, setModel] = useState("dinov2_base");
  const [classifier, setClassifier] = useState("logistic_regression");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pending = useRef(null);
  const submitTable = useCallback(async request => {
    setBusy(true);
    try {
      const {data} = await axios.post("/classification/train", {...token(), ...request});
      onSubmitted?.(data);
    } catch (e) { setError(String(errorText(e))); }
    finally { setBusy(false); }
  }, [onSubmitted]);
  const job = useJob(result => { if (pending.current) submitTable({...pending.current, table_name: result.table_name}); });
  const current = catalog.datasets.find(d => d.name === dataset);
  const fields = current?.label_fields || [];
  const ready = source === "dataset" ? !!current && fields.includes(field) && current.embeddings.includes(model)
    : catalog.tables.some(t => t.name === table && t.model_name === model);
  const submit = async event => {
    event.preventDefault(); setError("");
    if (source === "table") {submitTable({table_name: table, model, classifier}); return;}
    setBusy(true);
    try {
      const name = `dino_${Date.now()}_${Math.random().toString(16).slice(2,8)}`;
      pending.current = {model, classifier};
      const {data} = await axios.post("/classification/tables", {...token(), dataset_name: dataset,
        label_field: field, model, table_name: name}); job.start(data.job_id);
    } catch (e) {setError(String(errorText(e)));}
    finally {setBusy(false);}
  };
  const Container = embedded ? "div" : "details";
  return <Container className="classification-workflow">
    {!embedded && <summary>DINO Classification</summary>}
    <p>Train a classifier using existing image embeddings and classification labels.</p>
    <button type="button" onClick={refresh}>Refresh datasets and tables</button>
    <form onSubmit={submit}><fieldset disabled={busy || job.busy}>
      <label>Classification data source<select value={source} onChange={e => setSource(e.target.value)}>
        <option value="dataset">Image dataset</option><option value="table">Embedding table</option></select></label>
      {source === "dataset" ? <>
        <label>Classification dataset<select value={dataset} onChange={e => setDataset(e.target.value)}>
          <option value="">Choose dataset</option>{catalog.datasets.map(d => <option key={d.name}>{d.name}</option>)}</select></label>
        <label>Training label field<select value={field} onChange={e => setField(e.target.value)}>
          <option value="">Choose label field</option>{fields.map(f => <option key={f}>{f}</option>)}</select></label>
      </> : <label>Embedding table<select value={table} onChange={e => setTable(e.target.value)}>
        <option value="">Choose table</option>{catalog.tables.filter(t => t.model_name === model).map(t => <option key={t.name}>{t.name}</option>)}</select></label>}
      <label>Training DINO model<select value={model} onChange={e => setModel(e.target.value)}>
        {catalog.models.map(m => <option key={m.id} value={m.id}>{modelLabels[m.id] || m.model}</option>)}</select></label>
      <label>Classifier<select value={classifier} onChange={e => setClassifier(e.target.value)}>
        <option value="logistic_regression">Logistic Regression</option><option value="mlp">MLP</option></select></label>
      {source === "dataset" && current && !current.embeddings.includes(model) && <p>Extract embeddings for this model under Pre-Label → DINO first.</p>}
      <p>Automatic splits keep duplicate images together. Each class needs at least three distinct images. Keep multiple views of the same physical object in the same group before training.</p>
      <button className="org-btn org-btn-primary" type="submit" disabled={!ready}>Train Classifier</button>
    </fieldset></form>
    {job.status && <p role="status">Prepare training table: {job.status}</p>}
    {(error || job.error || catalogError) && <p role="alert">{error || job.error || catalogError}</p>}
  </Container>;
}
